import React, {
  useContext,
  useState,
  useCallback,
  useMemo,
  useEffect,
} from 'react';
import { useLazyQuery, useApolloClient } from '@apollo/client';
import styled from 'styled-components';
import Container from 'ls-common-client/dist/components/Container';
import Animation from 'ls-common-client/dist/components/Animation';
import SideBar from '../../components/UserManagement/SideBar';
import UserEmptyLayout from '../../components/UserManagement/UserEmptyLayout';
import ScrollContainer from '../../components/UserManagement/ScrollContainer';
import NotificationSlider from '../../components/NotificationSlider';
import { Context } from '../../context/AppContext';
import { MODALS } from '../../constants';
import UserReviewsGraphic from '../../assets/userManagement/userReviewGraphic.png';
import UserReviewsHeart from '../../assets/userManagement/userReviewHeart.png';
import UserReviewsEdit from '../../assets/userManagement/userReviewEdit.png';
import UserReviewsGraphicWebp from '../../assets/userManagement/userReviewGraphic.webp';
import reviewDeleted from '../../animations/reviewDeleted.json';
import listviewLoader from '../../animations/listviewLoader.json';
import { userReviewsQuery } from '../../gql/reviews';
import getNodes from '../../lib/getNodes';
import Review from './Review';
import useDataLayerPush from '../../hooks/useDataLayerPush';
import { DATA_PUSH } from '../../components/Search/constants';

const StyledContainer = styled(Container)`
  margin-top: 58px;
  padding: 0 30px;
  flex: 1;
  h1 {
    font-size: 30px;
    margin-bottom: 41px;
    line-height: 1.3em;
    font-weight: medium;
    @media (max-width: 767px) {
      font-size: 22px;
      margin-bottom: 20px;
    }
  }
  @media (max-width: 767px) {
    margin-top: 27px;
    padding: 0 11px;
  }
`;

const PAGE_SIZE = 10;

const Reviews = () => {
  const {
    modal: { showModal },
    user: { user },
    media: { mobile, desktop },
  } = useContext(Context);

  const client = useApolloClient();

  const { id: userId } = user || {};
  const [moreLoading, setMoreLoading] = useState();
  const [deleteSuccess, setDeleteSuccess] = useState();

  const [fetchReviews, { data, loading, fetchMore }] = useLazyQuery(
    userReviewsQuery,
    {
      errorPolicy: 'all',
      variables: {
        userId,
        first: PAGE_SIZE,
      },
    }
  );
  const { dataLayerPush } = useDataLayerPush();
  const dataToPush = {
    event: DATA_PUSH.EVENT.WRITE_A_REVIEW_CLICKED,
    cta_position_on_page: DATA_PUSH.CTA.REVIEWS_SECTION,
  };

  const { reviews } = data || {};
  const { pageInfo, edges } = reviews || {};
  const { endCursor, hasNextPage } = pageInfo || {};

  useEffect(() => {
    if (userId) {
      fetchReviews();
    }
  }, [userId]);

  const onNextPage = useCallback(async () => {
    if (hasNextPage && endCursor && !moreLoading) {
      setMoreLoading(true);
      await fetchMore({
        variables: {
          userId,
          first: PAGE_SIZE,
          after: endCursor,
        },
      });
      setMoreLoading(false);
    }
  }, [endCursor, userId, hasNextPage, moreLoading]);

  const writeToCache = newCache => {
    client.writeQuery({
      query: userReviewsQuery,
      data: {
        reviews: newCache,
      },
      variables: {
        userId,
        first: PAGE_SIZE,
      },
    });
  };

  const updateReviewCache = updatedReview => {
    const { id } = updatedReview;

    const newEdges = edges.map(review =>
      id === review.node.id
        ? {
            ...review,
            node: {
              ...review.node,
              ...updatedReview,
            },
          }
        : review
    );

    writeToCache({
      edges: newEdges,
      pageInfo,
    });
  };

  const refreshCursors = arr =>
    arr.map((edge, i) => ({
      ...edge,
      cursor: window.btoa(i),
    }));

  const addReviewToCache = newReview => {
    const newEdges = [...edges];

    newEdges.unshift({ node: newReview, __typename: 'ReviewEdge' });

    if (newEdges.length > PAGE_SIZE) {
      newEdges.pop();
    }

    writeToCache({
      edges: refreshCursors(newEdges),
      pageInfo,
    });
  };

  const removeReviewFromCache = id => {
    const newEdges = refreshCursors(edges.filter(({ node }) => node.id !== id));
    const lastEdge = [...newEdges].pop() || {};

    writeToCache({
      edges: newEdges,
      pageInfo: {
        ...pageInfo,
        endCursor: lastEdge.cursor,
      },
    });
  };

  const onDeleteSuccess = id => {
    removeReviewFromCache(id);
    setDeleteSuccess(true);
  };

  const list = useMemo(() => getNodes(edges || []) || [], [edges]);

  return (
    <>
      {(!userId || loading) && (
        <Container display="flex" justifyContent="center" alignItems="center">
          <Animation data={listviewLoader} width="300px" />
        </Container>
      )}
      {userId && !loading && !list.length && (
        <UserEmptyLayout
          heading="You haven't submitted any reviews yet!"
          text="Why not share your experiences with some businesses now?"
          buttonText="Write a Review"
          onClick={() => {
            showModal(MODALS.REVIEW_FORM, {
              disableDefaults: true,
              addReviewToCache,
            });
            dataLayerPush(dataToPush);
          }}
          graphic={UserReviewsGraphicWebp}
          graphicFallback={UserReviewsGraphic}
        />
      )}
      {userId && !loading && edges && !!edges.length && (
        <StyledContainer>
          <h1>{mobile ? '' : 'Your '}Reviews</h1>
          <Container display={!desktop ? 'block' : 'flex'}>
            <ScrollContainer
              id="UserReviews__list"
              onScrollToBottom={onNextPage}
            >
              {list.map((review, key) => (
                <Review
                  key={key}
                  review={review}
                  updateReviewCache={updateReviewCache}
                  onDeleteSuccess={onDeleteSuccess}
                />
              ))}
              {moreLoading && (
                <Container
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                >
                  <Animation data={listviewLoader} width="300px" />
                </Container>
              )}
            </ScrollContainer>
            <SideBar
              items={[
                {
                  title: 'Share the love...',
                  icon: UserReviewsHeart,
                  iconWidth: '50',
                  children: (
                    <>
                      <p>
                        Know a business who provides great service? Write them a
                        review!
                      </p>
                      <p>
                        <button
                          type="button"
                          onClick={() => {
                            showModal(MODALS.REVIEW_FORM, {
                              disableDefaults: true,
                              addReviewToCache,
                            });
                            dataLayerPush(dataToPush);
                          }}
                        >
                          Write a Review{' '}
                          <i className="icon-generalcaretrightsmall" />
                        </button>
                      </p>
                    </>
                  ),
                },
                {
                  title: 'Write the perfect review',
                  icon: UserReviewsEdit,
                  iconWidth: '47',
                  children: (
                    <>
                      <p>
                        Your review helps both locals and the business! Ensure
                        you include as much information as possible, including:
                      </p>
                      <ul>
                        <li>
                          <i className="icon-generalticklarge" />
                          <p>
                            The product and/or service you utilised the business
                            for.
                          </p>
                        </li>
                        <li>
                          <i className="icon-generalticklarge" />{' '}
                          <p>Roughly when you had your experience.</p>
                        </li>
                        <li>
                          <i className="icon-generalticklarge" />{' '}
                          <p>
                            Specific experiences of why you rated as you did.
                          </p>
                        </li>
                      </ul>
                      <p>
                        <a
                          href="https://business.localsearch.com.au/content-integrity/"
                          target="_blank"
                        >
                          View Review Guidelines{' '}
                          <i className="icon-generalcaretrightsmall" />
                        </a>
                      </p>
                    </>
                  ),
                },
              ]}
            />
          </Container>
        </StyledContainer>
      )}
      <NotificationSlider
        icon={
          <Container
            paddingLeft={mobile ? '0' : 'normal'}
            paddingTop={mobile ? '0' : 'normal'}
            paddingBottom={mobile ? '0' : 'normal'}
          >
            <Animation
              data={reviewDeleted}
              options={{
                loop: false,
              }}
              height={mobile ? '150px' : '80px'}
              width={mobile ? '150px' : '80px'}
            />
          </Container>
        }
        heading="Your review has been deleted."
        button="Return"
        buttonProps={{
          onClick: () => setDeleteSuccess(false),
          width: '161px',
        }}
        show={deleteSuccess}
        onClose={() => setDeleteSuccess(false)}
      />
    </>
  );
};

export default Reviews;
